import type { RemoteOrder, RemoteOrderItem } from '../api/ordersApi';
import type {
  OrderStatusHistoryEntry,
  StaticOrderDetail,
  StaticOrderLine,
} from './orderDetailStatic';
import { readPendingPaymentTracking } from './pendingPaymentContinuation';
import { normalizeOrderStatus } from './orderStatusPresentation';
import type { OrderStatus } from '../types';
import { env } from '@/shared/config/env';

function isLikelyUrl(value: string): boolean {
  return /^https?:\/\//i.test(value) || /^data:/i.test(value);
}

/** Resolve catalog-relative image paths the same way product gallery does. */
export function resolveOrderImageSrc(raw: string | undefined | null): string {
  const v = (raw ?? '').trim();
  if (!v || v === '-') return '';
  if (isLikelyUrl(v)) return v;
  const base = env.apiBaseUrl.replace(/\/v1\/?$/, '');
  const path = v.startsWith('/') ? v : `/${v}`;
  return `${base}${path}`;
}

/** Money fields on the wire are rupee major units; app renders integer paise. */
export function rupeesToPaise(amount: unknown): number {
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    return Math.round(amount * 100);
  }
  if (typeof amount === 'string' && amount.trim().length > 0) {
    const n = Number(amount);
    if (Number.isFinite(n)) return Math.round(n * 100);
  }
  return 0;
}

function nonemptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

/** Prefer multi-word initials; single word → first two letters. */
export function initialsFromName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
}

/** Ten-digit (or best-effort) phone for admin customer profile search. */
export function phoneDigitsForSearch(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  if (digits.length >= 10) return digits.slice(-10);
  return digits;
}

export function formatIndiaPhoneDisplay(raw: string | undefined): string {
  if (!raw?.trim()) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 11 && digits.startsWith('0')) {
    return formatIndiaPhoneDisplay(digits.slice(1));
  }
  return raw.trim();
}

function buildAddressLines(addr: Partial<RemoteOrder['deliveryAddress']>): string[] {
  const lines: string[] = [];
  const line1 = nonemptyString(addr.line1);
  const line2 = nonemptyString(addr.line2);
  if (line1) lines.push(line1);
  if (line2) lines.push(line2);

  const city = nonemptyString(addr.city);
  const state = nonemptyString(addr.state);
  const pincode = nonemptyString(addr.pincode);
  const locality = [city, state].filter(Boolean).join(', ');
  const cityLine = [locality, pincode].filter(Boolean).join(' — ');
  if (cityLine) lines.push(cityLine);

  return lines;
}

/**
 * Known payment method wire values → i18n key under `orders`.
 * Unknowns fall back to prepaid-style label via generic key + display of wire value.
 */
export function paymentMethodLabelKey(method: string | undefined): string {
  const m = (method ?? '').trim().toUpperCase();
  switch (m) {
    case 'PREPAID':
      return 'detail.payment.methodPrepaid';
    case 'COD':
    case 'CASH_ON_DELIVERY':
      return 'detail.payment.methodCod';
    case 'UPI':
      return 'detail.payment.methodUpi';
    case 'CARD':
      return 'detail.payment.methodCard';
    case 'WALLET':
      return 'detail.payment.methodWallet';
    default:
      return method?.trim() ? 'detail.payment.methodOther' : 'detail.payment.methodUnknown';
  }
}

function isPaidPaymentMethod(method: string | undefined, paymentStatus?: string): boolean {
  const ps = (paymentStatus ?? '').trim().toUpperCase();
  if (ps === 'COMPLETED' || ps === 'PAID' || ps === 'SUCCESS') return true;
  if (
    ps === 'PENDING' ||
    ps === 'PENDING_COLLECTION' ||
    ps === 'FAILED' ||
    ps === 'EXPIRED' ||
    ps === 'CANCELLED' ||
    ps === 'CANCELED'
  ) {
    return false;
  }

  const m = (method ?? '').trim().toUpperCase();
  if (!m) return false;
  if (m === 'COD' || m === 'CASH_ON_DELIVERY') return false;
  // PREPAID / UPI / CARD / WALLET etc. treated as already settled only when status is unknown
  return true;
}

/** `collectedAmountPaisa` is integer paise (string or number). */
export function parsePaisaAmount(raw: unknown): number | undefined {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
    return Math.round(raw);
  }
  if (typeof raw === 'string' && raw.trim().length > 0) {
    const n = Number(raw.trim());
    if (Number.isFinite(n) && n >= 0) return Math.round(n);
  }
  return undefined;
}

function pickString(source: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = nonemptyString(source[key]);
    if (value) return value;
  }
  return undefined;
}

function mapStatusHistory(raw: unknown): OrderStatusHistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const o = entry as Record<string, unknown>;
    const createdAtIso = pickString(o, 'createdAt', 'created_at');
    if (!createdAtIso) return [];
    return [
      {
        fromStatus: pickString(o, 'fromStatus', 'from_status') ?? '',
        toStatus: pickString(o, 'toStatus', 'to_status') ?? '',
        notes: pickString(o, 'notes') ?? '',
        createdAtIso,
        changedBy: pickString(o, 'changedBy', 'changed_by') ?? '',
      },
    ];
  });
}

function mapLine(item: Partial<RemoteOrderItem>, idx: number): StaticOrderLine {
  const name = nonemptyString(item.productName) ?? '—';
  const imageSrc = resolveOrderImageSrc(item.productImageUrl);
  return {
    id: nonemptyString(item.id) ?? `line-${idx}`,
    name,
    sku: nonemptyString(item.productId) ?? '—',
    imageSrc,
    imageAlt: name,
    quantity: typeof item.quantity === 'number' ? item.quantity : 0,
    pickedQuantity: typeof item.pickedQuantity === 'number' ? item.pickedQuantity : undefined,
    unitPricePaise: rupeesToPaise(item.unitPrice),
    lineTotalPaise: rupeesToPaise(item.totalPrice),
    mrpPaise: typeof item.mrp === 'number' ? rupeesToPaise(item.mrp) : undefined,
    status: nonemptyString(item.status),
  };
}

export function mapRemoteOrderToDetail(
  dto: Partial<RemoteOrder> & {
    items?: unknown[];
    paymentMethod?: string;
    assignedDeliveryAgent?: string;
    customerName?: string;
    customerPhone?: string;
    paymentStatus?: string;
    collectionMode?: string;
    collectionStatus?: string;
    collectedAmountPaisa?: string | number;
    returnedAt?: string;
    returnReason?: string;
    statusHistory?: unknown;
  },
  idOrNumber: string,
): StaticOrderDetail {
  const placedAtIso = nonemptyString(dto.createdAt) ?? new Date(0).toISOString();
  const promisedMinutes =
    typeof dto.promisedDeliveryMinutes === 'number' && Number.isFinite(dto.promisedDeliveryMinutes)
      ? dto.promisedDeliveryMinutes
      : 0;
  const targetDeliveryAtIso = new Date(
    new Date(placedAtIso).getTime() + promisedMinutes * 60_000,
  ).toISOString();

  const rawItems = Array.isArray(dto.items) ? dto.items : [];
  const lines = rawItems.map((item, idx) =>
    mapLine((item ?? {}) as Partial<RemoteOrderItem>, idx),
  );

  const deliveryAddress =
    typeof dto.deliveryAddress === 'object' && dto.deliveryAddress !== null
      ? (dto.deliveryAddress as Partial<RemoteOrder['deliveryAddress']> & {
          phoneNumber?: string;
          customerName?: string;
        })
      : {};

  const customerName =
    nonemptyString(dto.customerName) ??
    nonemptyString(deliveryAddress.customerName) ??
    nonemptyString(deliveryAddress.label) ??
    nonemptyString(dto.userId) ??
    '—';

  const phoneRaw =
    nonemptyString(dto.customerPhone) ?? nonemptyString(deliveryAddress.phoneNumber);
  const phoneDisplay = formatIndiaPhoneDisplay(phoneRaw);
  const phoneSearch = phoneDigitsForSearch(phoneRaw);

  const addressLines = buildAddressLines(deliveryAddress);
  const addressLabel = nonemptyString(deliveryAddress.label);

  const orderId =
    nonemptyString(dto.id) ?? nonemptyString(dto.orderNumber) ?? idOrNumber;
  const orderNumber = nonemptyString(dto.orderNumber) ?? orderId;
  const status: OrderStatus =
    typeof dto.status === 'string' ? normalizeOrderStatus(dto.status) : normalizeOrderStatus('');

  const assignedDeliveryAgentName =
    nonemptyString(dto.assignedDeliveryAgentName) ??
    nonemptyString(dto.assignedDeliveryAgent) ??
    '';

  const paymentMethod = nonemptyString(dto.paymentMethod) ?? '';
  const paymentStatus = nonemptyString(dto.paymentStatus) ?? '';
  const collectionMode = nonemptyString(dto.collectionMode) ?? '';
  const collectionStatus = nonemptyString(dto.collectionStatus) ?? '';
  const collectedAmountPaise = parsePaisaAmount(dto.collectedAmountPaisa);
  const methodLabelKey = paymentMethodLabelKey(paymentMethod);
  const isPaid = isPaidPaymentMethod(paymentMethod, paymentStatus);

  const notes = nonemptyString(dto.notes) ?? '';
  const confirmedAtIso = nonemptyString(dto.confirmedAt) ?? '';
  const deliveredAtIso = nonemptyString(dto.deliveredAt) ?? '';
  const cancelledAtIso = nonemptyString(dto.cancelledAt) ?? '';
  const cancellationReason = nonemptyString(dto.cancellationReason) ?? '';
  const returnedAtIso = nonemptyString(dto.returnedAt) ?? '';
  const returnReason = nonemptyString(dto.returnReason) ?? '';
  const statusHistory = mapStatusHistory(dto.statusHistory);
  const pendingPayment = readPendingPaymentTracking(dto);
  const slaPickingStartedAtIso = nonemptyString(dto.slaPickingStartedAt) ?? '';
  const slaBreached = Boolean(dto.slaBreached);

  let logisticsStatus: StaticOrderDetail['logistics']['status'] = 'none';
  if (assignedDeliveryAgentName) {
    logisticsStatus = 'assigned';
  } else if (
    status === 'PACKED' ||
    status === 'DISPATCHED' ||
    status === 'PARTIALLY_FULFILLED' ||
    status === 'PICKING' ||
    status === 'RETURNED'
  ) {
    logisticsStatus = 'assigning';
  }

  return {
    id: orderId,
    orderNumber,
    status,
    placedAtIso,
    confirmedAtIso,
    deliveredAtIso,
    cancelledAtIso,
    cancellationReason,
    returnedAtIso,
    returnReason,
    statusHistory,
    pendingPaymentResolution: pendingPayment.resolution,
    pendingPaymentContinuedBy: pendingPayment.continuedBy,
    pendingPaymentContinuedAt: pendingPayment.continuedAtIso,
    promisedDeliveryMinutes: promisedMinutes,
    slaBreached,
    slaPickingStartedAtIso,
    assignedDeliveryAgentName,
    hubName: nonemptyString(dto.storeName) ?? '—',
    hubCode: '',
    storeId: nonemptyString(dto.storeId) ?? '',
    targetDeliveryAtIso,
    priorityLabelKey: 'detail.quick.prioritySla',
    totalItemCount: lines.reduce((s, l) => s + (l.quantity || 0), 0),
    lines,
    subtotalPaise: rupeesToPaise(dto.subtotal),
    deliveryFeePaise: rupeesToPaise(dto.deliveryFee),
    handlingFeePaise: rupeesToPaise(dto.handlingFee),
    taxPaise: rupeesToPaise(dto.taxAmount),
    discountPaise: rupeesToPaise(dto.discountAmount),
    totalPaise: rupeesToPaise(dto.totalAmount),
    customer: {
      initials: initialsFromName(customerName === '—' ? '' : customerName),
      name: customerName,
      phoneDisplay: phoneDisplay || '—',
      phoneSearch,
      addressLabel,
      addressLines: addressLines.length > 0 ? addressLines : ['—'],
      deliveryNote: notes,
    },
    lifecycle: {
      pickingElapsedLabelKey: 'detail.lifecycle.pickingInProgress',
    },
    payment: {
      method: paymentMethod,
      methodLabelKey,
      paymentStatus,
      collectionMode,
      collectionStatus,
      collectedAmountPaise,
      transactionId: '',
      isPaid,
    },
    logistics: {
      status: logisticsStatus,
      statusLabelKey:
        logisticsStatus === 'assigned'
          ? 'detail.logistics.assigned'
          : logisticsStatus === 'assigning'
            ? 'detail.logistics.assigning'
            : 'detail.logistics.none',
    },
  };
}
